import type {
  CreateDrillRequest,
  DrillView,
  IntensityLevel,
  PlanBlockInput,
  PlanVersionView,
  SupportedLocale,
  UpdatePlanVersionRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';
import { wholeNumber } from './scheduling';

export const intensityKeys: Readonly<Record<IntensityLevel, TranslationKey>> = {
  low: 'training.intensity.low',
  medium: 'training.intensity.medium',
  high: 'training.intensity.high',
};

export const planStateKeys: Readonly<Record<'draft' | 'published' | 'archived', TranslationKey>> = {
  draft: 'training.plan.state.draft',
  published: 'training.plan.state.published',
  archived: 'training.plan.state.archived',
};

/** A club-authored title in the reader's language, falling back to English, then to any language. */
export function drillTitle(titles: DrillView['titles'], locale: SupportedLocale): string {
  return titles[locale] ?? titles.en ?? titles.hu ?? titles.ro ?? '';
}

/** The department choice that makes a drill available to the whole club. */
export const wholeClub = 'club';

export interface DrillForm {
  departmentId: string;
  title: string;
  minutes: string;
  equipment: string;
  instructions: string;
  protocol: string;
}

export const emptyDrillForm = (departmentId: string): DrillForm => ({
  departmentId,
  title: '',
  minutes: '10',
  equipment: '',
  instructions: '',
  protocol: '',
});

export function drillFormOf(drill: DrillView, locale: SupportedLocale): DrillForm {
  return {
    departmentId: drill.departmentId ?? wholeClub,
    title: drillTitle(drill.titles, locale),
    minutes: String(Math.round(drill.durationSeconds / 60)),
    equipment: drill.equipment.join(', '),
    instructions: drill.instructions ?? '',
    protocol: drill.protocol ?? '',
  };
}

export function canSubmitDrill(form: DrillForm): boolean {
  const minutes = wholeNumber(form.minutes);
  return form.title.trim() !== '' && minutes !== null && minutes >= 1 && minutes <= 240;
}

function equipmentList(text: string): string[] {
  return text
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/**
 * The title is stored under the language the author is writing in. A new version keeps the titles
 * written in other languages, so translating a drill never loses an earlier translation.
 */
export function drillRequestBody(
  form: DrillForm,
  locale: SupportedLocale,
  existing: DrillView['titles'] = {},
): CreateDrillRequest {
  return {
    departmentId:
      form.departmentId === '' || form.departmentId === wholeClub ? null : form.departmentId,
    titles: { ...existing, [locale]: form.title.trim() },
    protocol: form.protocol.trim() === '' ? null : form.protocol.trim(),
    instructions: form.instructions.trim() === '' ? null : form.instructions.trim(),
    durationSeconds: (wholeNumber(form.minutes) ?? 1) * 60,
    equipment: equipmentList(form.equipment),
  };
}

export interface StationForm {
  label: string;
  minutes: string;
}

export interface BlockForm {
  objective: string;
  minutes: string;
  drillVersionId: string;
  intensity: IntensityLevel | '';
  equipment: string;
  surface: string;
  coachNotes: string;
  familyVisible: boolean;
  familySummary: string;
  stations: StationForm[];
}

export interface PlanForm {
  departmentId: string;
  title: string;
  objective: string;
  sessionMinutes: string;
  preparationMinutes: string;
  breakMinutes: string;
  overrideReason: string;
  blocks: BlockForm[];
}

export const emptyBlockForm = (): BlockForm => ({
  objective: '',
  minutes: '10',
  drillVersionId: '',
  intensity: '',
  equipment: '',
  surface: '',
  coachNotes: '',
  familyVisible: false,
  familySummary: '',
  stations: [],
});

export const emptyPlanForm = (departmentId: string): PlanForm => ({
  departmentId,
  title: '',
  objective: '',
  sessionMinutes: '90',
  preparationMinutes: '5',
  breakMinutes: '5',
  overrideReason: '',
  blocks: [emptyBlockForm()],
});

export function planFormOf(version: PlanVersionView): PlanForm {
  return {
    departmentId: version.departmentId,
    title: version.title,
    objective: version.objective ?? '',
    sessionMinutes: String(Math.round(version.plannedSessionSeconds / 60)),
    preparationMinutes: String(Math.round(version.preparationSeconds / 60)),
    breakMinutes: String(Math.round(version.breakSeconds / 60)),
    overrideReason: version.overrideReason ?? '',
    blocks: version.blocks.map((block) => ({
      objective: block.objective,
      minutes: String(Math.round(block.durationSeconds / 60)),
      drillVersionId: block.drillVersionId ?? '',
      intensity: block.intensity ?? '',
      equipment: block.equipment ?? '',
      surface: block.surface ?? '',
      coachNotes: block.coachNotes ?? '',
      familyVisible: block.familyVisible,
      familySummary: block.familySummary ?? '',
      stations: block.stations.map((station) => ({
        label: station.label,
        minutes: String(Math.round(station.durationSeconds / 60)),
      })),
    })),
  };
}

const minutesToSeconds = (text: string): number => (wholeNumber(text) ?? 0) * 60;

/** A block with parallel stations lasts as long as its longest station, not their sum. */
export function blockSeconds(block: BlockForm): number {
  if (block.stations.length === 0) return minutesToSeconds(block.minutes);
  return Math.max(...block.stations.map((station) => minutesToSeconds(station.minutes)));
}

export function activeSeconds(form: PlanForm): number {
  return form.blocks.reduce((total, block) => total + blockSeconds(block), 0);
}

export function availableSeconds(form: PlanForm): number {
  return Math.max(
    0,
    minutesToSeconds(form.sessionMinutes) -
      minutesToSeconds(form.preparationMinutes) -
      minutesToSeconds(form.breakMinutes),
  );
}

export function planFitsForm(form: PlanForm): boolean {
  return activeSeconds(form) <= availableSeconds(form);
}

export function canSubmitPlan(form: PlanForm): boolean {
  if (form.title.trim() === '' || form.departmentId === '') return false;
  const session = wholeNumber(form.sessionMinutes);
  if (session === null || session < 1 || session > 480) return false;
  if (wholeNumber(form.preparationMinutes) === null || wholeNumber(form.breakMinutes) === null) {
    return false;
  }
  if (form.blocks.length === 0) return false;
  const valid = form.blocks.every((block) => {
    if (block.objective.trim() === '') return false;
    if (block.familyVisible && block.familySummary.trim() === '') return false;
    if (block.stations.length > 0) {
      return block.stations.every(
        (station) => station.label.trim() !== '' && (wholeNumber(station.minutes) ?? 0) >= 1,
      );
    }
    return (wholeNumber(block.minutes) ?? 0) >= 1;
  });
  // A plan that does not fit needs a reason before it can be saved.
  return valid && (planFitsForm(form) || form.overrideReason.trim() !== '');
}

function blockInput(block: BlockForm): PlanBlockInput {
  return {
    objective: block.objective.trim(),
    drillVersionId: block.drillVersionId === '' ? null : block.drillVersionId,
    durationSeconds: block.stations.length > 0 ? 0 : minutesToSeconds(block.minutes),
    intensity: block.intensity === '' ? null : block.intensity,
    equipment: block.equipment.trim() === '' ? null : block.equipment.trim(),
    surface: block.surface.trim() === '' ? null : block.surface.trim(),
    coachNotes: block.coachNotes.trim() === '' ? null : block.coachNotes.trim(),
    familyVisible: block.familyVisible,
    familySummary:
      block.familyVisible && block.familySummary.trim() !== '' ? block.familySummary.trim() : null,
    stations: block.stations.map((station) => ({
      label: station.label.trim(),
      drillVersionId: null,
      durationSeconds: minutesToSeconds(station.minutes),
    })),
  };
}

export function planRequestBody(form: PlanForm): UpdatePlanVersionRequest {
  return {
    title: form.title.trim(),
    objective: form.objective.trim() === '' ? null : form.objective.trim(),
    plannedSessionSeconds: minutesToSeconds(form.sessionMinutes),
    preparationSeconds: minutesToSeconds(form.preparationMinutes),
    breakSeconds: minutesToSeconds(form.breakMinutes),
    overrideReason: form.overrideReason.trim() === '' ? null : form.overrideReason.trim(),
    blocks: form.blocks.map(blockInput),
  };
}

/** Moves a block one place; moving past either end changes nothing. */
export function moveBlock(
  blocks: readonly BlockForm[],
  index: number,
  offset: -1 | 1,
): BlockForm[] {
  const target = index + offset;
  const moving = blocks[index];
  const other = blocks[target];
  if (moving === undefined || other === undefined) return [...blocks];
  const next = [...blocks];
  next[index] = other;
  next[target] = moving;
  return next;
}

export function minutesLabel(seconds: number): string {
  return String(Math.round(seconds / 60));
}
